/**
 * CategoryRail — the left vertical strip of category buttons with an active marker.
 */

import { Pressable, ScrollView, View } from 'react-native';
import { Lock } from 'lucide-react-native';
import { darkTheme } from '../../../src/theme/themes';
import { CATALOG } from './characterCustomizer.constants';
import { CategoryIcon } from './CategoryIcon';
import { categoryRailStyles as styles } from './CategoryRail.styles';
import type { CustomizerCategoryId } from './characterCustomizer.types';

interface CategoryRailProps {
  categories: CustomizerCategoryId[];
  activeCategory: CustomizerCategoryId;
  /** Categories shown greyed with a lock; tapping them calls onLockedPress instead of onSelect. */
  lockedCategories?: ReadonlySet<CustomizerCategoryId>;
  onSelect: (category: CustomizerCategoryId) => void;
  onLockedPress?: (category: CustomizerCategoryId) => void;
}

export function CategoryRail({
  categories,
  activeCategory,
  lockedCategories,
  onSelect,
  onLockedPress,
}: CategoryRailProps) {
  return (
    <ScrollView style={styles.rail} contentContainerStyle={styles.railContent} showsVerticalScrollIndicator={false}>
      {categories.map((category) => {
        const locked = lockedCategories?.has(category) ?? false;
        const active = !locked && category === activeCategory;
        return (
          <Pressable
            key={category}
            onPress={() => (locked ? onLockedPress?.(category) : onSelect(category))}
            accessibilityRole="tab"
            accessibilityState={{ selected: active, disabled: locked }}
            accessibilityLabel={
              locked ? `${CATALOG[category].label} (locked)` : CATALOG[category].label
            }
            style={[
              styles.button,
              active ? styles.buttonActive : null,
              locked ? styles.buttonLocked : null,
            ]}
          >
            {active ? <View style={styles.marker} /> : null}
            <CategoryIcon
              category={category}
              size={22}
              color={active ? darkTheme.primary : darkTheme.gray[400]}
            />
            {locked ? (
              <View style={styles.lockBadge}>
                <Lock size={10} color={darkTheme.gray[300]} strokeWidth={2.5} />
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
